import * as React from "react";
import { Button, Dropdown, notification } from "antd";
import { Kind } from "../../../schema/types/base";
import { itemHelpers as itemHelpersBase } from "../../../mst/kinds/item.helpers";
import { TerraformImportModal } from "../../../components/modals/tfImportModal";
import { ItemType } from "antd/es/menu/interface";
import { K8sCrdExportModal } from "../../../components/modals/k8sCrdExportModal";
import { useAppSelector } from "../../../store/hooks";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { gvcScopedKinds } from "../../../services/utils";
import { DownOutlined } from "@ant-design/icons";
import { selectContext } from "../../../store/contextSlice";

interface IProps {
  kind: Kind;
  selectionLinks: string[];
  options?: { key: string; group?: string; label: string; danger?: boolean; disabled?: boolean; callback: Function }[];
}
export const TableActionsDropdown = ({ kind, selectionLinks, options = [] }: IProps) => {
  const context = useAppSelector(selectContext);
  const terraformSupportedKinds = useAppSelector((state) => state.rest.terraformSupportedKinds);
  const k8sCrdSupportedKinds = useAppSelector((state) => state.rest.k8sCrdSupportedKinds);
  const theme = useAppSelector((state) => state.theme.theme);

  const [isLoading, setIsLoading] = React.useState(false);
  const [terraformImportCommand, setTerraformImportCommand] = React.useState("");
  const [isK8sCrdExportModalOpen, setIsK8sCrdExportModalOpen] = React.useState<boolean>(false);

  // For GVC-scoped kinds, only include GVC in filename if all items are from the same GVC
  let gvcForFilename = context.gvc;
  if (gvcScopedKinds.includes(kind) && selectionLinks.length > 0) {
    const gvcSet = new Set(selectionLinks.map(link => parseLink(link, { useInputCtx: true }).gvc).filter(Boolean));
    gvcForFilename = gvcSet.size === 1 ? Array.from(gvcSet)[0] : "";
  }
  const itemHelpers = itemHelpersBase({ org: context.org, gvc: gvcForFilename, kind: kind });

  const canExportToTerraform = terraformSupportedKinds.includes(kind as any);
  const canExportToK8sCrd = k8sCrdSupportedKinds.includes(kind as any);
  const keyFunctionMap: { [_: string]: Function } = {
    "export-json": () => itemHelpers.handleExportJSON(selectionLinks, false),
    "export-yaml": () => itemHelpers.handleExportYaml(selectionLinks, false),
    "export-json-slim": () => itemHelpers.handleExportJSON(selectionLinks, true),
    "export-yaml-slim": () => itemHelpers.handleExportYaml(selectionLinks, true),
    "export-terraform": () => itemHelpers.handleExportTerraform(selectionLinks),
    "export-k8s-crd": () => setIsK8sCrdExportModalOpen(true),
    "export-csv": () => itemHelpers.handleExportCsv(selectionLinks),
  };
  const menuExportItems = [
    { key: "export-json", label: "JSON" },
    { key: "export-yaml", label: "YAML" },
    { key: "export-json-slim", label: "JSON Slim" },
    { key: "export-yaml-slim", label: "YAML Slim" },
  ];
  if (canExportToTerraform) {
    menuExportItems.push({ key: "export-terraform", label: "Terraform" });
  }
  if (canExportToK8sCrd) {
    menuExportItems.push({ key: "export-k8s-crd", label: "K8s CRD" });
  }
  if (kind === "user") {
    menuExportItems.push({ key: "export-csv", label: "CSV" });
  }
  const items: ItemType[] = [
    {
      key: "ignore-export",
      label: "Export",
      children: menuExportItems,
    },
  ];
  for (const option of options) {
    keyFunctionMap[option.key] = option.callback;
    if (!!option.group) {
      const groupKey = option.group.replaceAll(" ", "-").toLowerCase();
      let itemGroup = items.find((item) => item?.key === groupKey);
      if (!itemGroup) {
        items.push({ key: groupKey, label: option.group, children: [] });
        itemGroup = items.find((item) => item?.key === groupKey);
      }
      (itemGroup! as any).children.push({
        key: option.key,
        label: option.label,
        danger: option.danger || false,
        disabled: option.disabled || false,
      });
    } else {
      items.push({
        key: option.key,
        label: option.label,
        danger: option.danger || false,
        disabled: option.disabled || false,
      });
    }
  }

  async function handleActionsMenuClick(e: any) {
    try {
      setIsLoading(true);
      const fn = keyFunctionMap[e.key];
      const response = await fn();

      if (e.key == "export-terraform") {
        setTerraformImportCommand(response);
      }
    } catch (e) {
      notification.error({ message: "Failed", description: e.message });
    }
    setIsLoading(false);
  }

  return (
    <>
      <Dropdown
        menu={{ onClick: handleActionsMenuClick, items: items }}
        placement={"bottom"}
        trigger={["click"]}
        disabled={isLoading}
      >
        <Button loading={isLoading} disabled={isLoading} type={"primary"} iconPosition="end" icon={<DownOutlined />}>
          Actions
        </Button>
      </Dropdown>
      {terraformImportCommand ? (
        <TerraformImportModal command={terraformImportCommand} onClose={() => setTerraformImportCommand("")} />
      ) : null}
      {isK8sCrdExportModalOpen ? (
        <K8sCrdExportModal
          kind={kind}
          links={selectionLinks}
          onClose={() => {
            setIsK8sCrdExportModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
